import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Gallery-style menu panel opened by the header "Menu" button. First-level items
 * with children open their groups beside them; hovering or focusing a first-level
 * item shows its picture; Search opens a typed search line. The page stays
 * scrollable. Escape, the close sign, the backdrop or moving focus out of the menu
 * closes it, and focus returns to the "Menu" button on Escape.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - "Menu" button
 * @property {HTMLElement} panel - The panel
 * @property {HTMLElement} backdrop - Shade over the page
 * @property {HTMLElement[]} [items] - First-level items
 * @property {HTMLElement[]} [groups] - Group sets, matched to items by data-index
 * @property {HTMLElement[]} [images] - Item pictures, matched to items by data-index
 * @property {HTMLElement} [defaultImage] - Picture shown when no item picture applies
 * @property {HTMLButtonElement} [closeButton] - Close sign in the header row
 * @property {HTMLButtonElement} [searchToggle] - Search item
 * @property {HTMLFormElement} [search] - Typed search line
 * @property {HTMLInputElement} [searchInput] - Search field
 *
 * @extends {Component<Refs>}
 */
class FifiMenuComponent extends Component {
  requiredRefs = ['toggle', 'panel', 'backdrop'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#hideTimer);
  }

  get isOpen() {
    return this.refs.toggle.getAttribute('aria-expanded') === 'true';
  }

  /** Opens or closes the panel. */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const { toggle, panel, backdrop } = this.refs;
    clearTimeout(this.#hideTimer);

    panel.hidden = false;
    backdrop.hidden = false;
    if (this.refs.closeButton) this.refs.closeButton.hidden = false;
    this.#place();
    toggle.setAttribute('aria-expanded', 'true');
    this.#showPicture(-1);

    // Two frames so the panel is laid out before it starts to unfold
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.close();
        this.refs.toggle.focus();
      },
      { signal }
    );

    // The page keeps scrolling under the shade, and under the panel when the panel
    // has nothing of its own to scroll
    backdrop.addEventListener('wheel', scrollPageWithWheel, { passive: true, signal });
    panel.addEventListener(
      'wheel',
      (event) => {
        if (panel.scrollHeight <= panel.clientHeight) scrollPageWithWheel(event);
      },
      { passive: true, signal }
    );

    // The page stays scrollable; keep the panel lined up under the header row
    const replace = () => this.#place();
    window.addEventListener('scroll', replace, { passive: true, signal });
    document.addEventListener('scroll', replace, { passive: true, capture: true, signal });
    window.addEventListener('resize', replace, { passive: true, signal });

    document.addEventListener(
      'focusin',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.close();
      },
      { signal }
    );
  }

  close() {
    if (!this.isOpen) return;

    const { toggle, panel, backdrop } = this.refs;
    this.#controller?.abort();
    this.#controller = null;

    toggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    if (this.refs.closeButton) this.refs.closeButton.hidden = true;
    this.#closeGroups();
    this.#closeSearch();

    this.#hideTimer = setTimeout(
      () => {
        panel.hidden = true;
        backdrop.hidden = true;
      },
      prefersReducedMotion() ? 0 : 320
    );
  }

  /**
   * Lines the panel up under the header row. The sticky header can become the
   * containing block of fixed descendants, so the offset is measured against it.
   */
  #place() {
    const { panel } = this.refs;
    const row = this.closest('.header__row') ?? this;
    const rowBottom = Math.max(0, row.getBoundingClientRect().bottom);

    this.style.setProperty('--fifi-menu-top', '0px');
    const containerTop = panel.getBoundingClientRect().top;

    this.style.setProperty('--fifi-menu-top', `${Math.round(rowBottom - containerTop)}px`);
    this.style.setProperty('--fifi-menu-space', `${Math.round(window.innerHeight - rowBottom)}px`);
  }

  /**
   * Opens the groups of a first-level item, or closes them when already open.
   * @param {Event} event
   */
  openGroup(event) {
    const item = event.target;
    if (!(item instanceof HTMLElement)) return;

    const wasOpen = item.getAttribute('aria-expanded') === 'true';
    this.#closeGroups();
    this.#closeSearch();
    if (wasOpen) return;

    const index = item.dataset.index;
    const group = this.refs.groups?.find((element) => element.dataset.index === index);
    if (!group) return;

    item.setAttribute('aria-expanded', 'true');
    group.hidden = false;
  }

  /**
   * Shows the picture for a hovered or focused first-level item.
   * @param {Event} event
   */
  preview(event) {
    const item = event.target;
    if (!(item instanceof HTMLElement)) return;

    this.#showPicture(Number(item.dataset.index));
  }

  /** Opens or closes the typed search line beside the Search item. */
  toggleSearch() {
    const { search, searchToggle, searchInput } = this.refs;
    if (!search || !searchToggle) return;

    const opening = search.hidden;
    this.#closeGroups();
    search.hidden = !opening;
    searchToggle.setAttribute('aria-expanded', String(opening));
    if (opening) searchInput?.focus();
  }

  #closeSearch() {
    const { search, searchToggle } = this.refs;
    if (search) search.hidden = true;
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
  }

  #closeGroups() {
    for (const item of this.refs.items ?? []) {
      if (item.hasAttribute('aria-expanded')) item.setAttribute('aria-expanded', 'false');
    }
    for (const group of this.refs.groups ?? []) group.hidden = true;
  }

  /** @param {number} index - Item index, or -1 for the default picture */
  #showPicture(index) {
    const images = this.refs.images ?? [];
    const match = images.find((image) => Number(image.dataset.index) === index);

    for (const image of images) image.hidden = image !== match;
    if (this.refs.defaultImage) this.refs.defaultImage.hidden = Boolean(match);
  }
}

/**
 * Keeps the page scrolling under the menu. On large screens the theme scrolls the
 * page inside .page-wrapper, which a fixed layer would otherwise cut off from the
 * wheel; elsewhere the document scrolls by itself.
 * @param {WheelEvent} event
 */
function scrollPageWithWheel(event) {
  const scroller = document.querySelector('.page-wrapper');
  if (!(scroller instanceof HTMLElement) || scroller.scrollHeight <= scroller.clientHeight) return;

  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
  scroller.scrollBy({ top: event.deltaY * unit, left: 0, behavior: 'instant' });
}

if (!customElements.get('fifi-menu-component')) {
  customElements.define('fifi-menu-component', FifiMenuComponent);
}
